// Cómo se "abre" un archivo desde /almacenamiento (round 2026-10-28) y si la pantalla puede
// ampliar una miniatura al pasar el mouse. Puro y sin React: lo comparten la lista, el detalle y
// la página, y se prueba aparte.
//
// Regla de datos de la app: abrir cuenta como una lectura normal de /files (misma URL firmada que
// ya trae el listado, con la sesión por cookie). Nada de esto pide un archivo antes de que la
// persona lo pida con un clic: el hover usa solo la miniatura que la fila ya muestra.
import type { Archivo } from "../../services/almacenamiento.api";
import { descargarVerificado } from "../../utils/descargaArchivo";
import { urlDeDescarga } from "./almacenamientoUtils";

/**
 * - `imagen`: visor dentro de la página (modal, tamaño de pantalla).
 * - `video`: visor dentro de la página con `<VideoPlayer>` (versión liviana con paso a HD).
 * - `pdf`: pestaña nueva con la URL firmada (el navegador lo muestra).
 * - `descarga`: Office, texto y demás: el navegador no los muestra, se descargan.
 * - `ninguno`: no hay nada que abrir (referencia rota, versión liviana sin video, sin enlace).
 */
export type ModoApertura = "imagen" | "video" | "pdf" | "descarga" | "ninguno";

export interface Apertura {
  modo: ModoApertura;
  /** Solo con `modo === "ninguno"`: por qué no se puede abrir. */
  motivo: string | null;
}

type DatosApertura = Pick<
  Archivo,
  "tipo" | "mime" | "nombre" | "url" | "estado" | "soloVersionLiviana"
>;

export const MOTIVO_ROTO =
  "El archivo ya no existe en el disco: solo queda la referencia en el registro.";
export const MOTIVO_SOLO_LIVIANA =
  "Es la copia liviana de un video que ya no existe: no hay nada que abrir.";
export const MOTIVO_SIN_ENLACE = "Este archivo no tiene un enlace disponible para abrirlo.";

const esPdf = (a: Pick<Archivo, "mime" | "nombre">): boolean =>
  (a.mime || "").toLowerCase().includes("pdf") || /\.pdf$/i.test(a.nombre || "");

/** Decide cómo se abre un archivo (o por qué no se puede). */
export function aperturaDeArchivo(a: DatosApertura): Apertura {
  if (a.estado === "roto") return { modo: "ninguno", motivo: MOTIVO_ROTO };
  if (a.soloVersionLiviana) return { modo: "ninguno", motivo: MOTIVO_SOLO_LIVIANA };
  if (!a.url) return { modo: "ninguno", motivo: MOTIVO_SIN_ENLACE };
  if (a.tipo === "image") return { modo: "imagen", motivo: null };
  if (a.tipo === "video") return { modo: "video", motivo: null };
  if (esPdf(a)) return { modo: "pdf", motivo: null };
  return { modo: "descarga", motivo: null };
}

/** Texto del tooltip del botón «Abrir»: qué va a pasar al hacer clic (o por qué no se puede). */
export function textoAccionAbrir(ap: Apertura): string {
  switch (ap.modo) {
    case "imagen":
      return "Ver la imagen";
    case "video":
      return "Reproducir el video";
    case "pdf":
      return "Abrir el PDF en una pestaña nueva";
    case "descarga":
      return "Este formato no se puede ver aquí: se descarga";
    default:
      return ap.motivo ?? MOTIVO_SIN_ENLACE;
  }
}

/** ¿El dispositivo tiene un puntero que pasa «por encima» (mouse)? En táctiles no hay hover. */
export function puedeHover(): boolean {
  try {
    return typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches === true;
  } catch {
    return false;
  }
}

/** PDF: pestaña nueva con la URL firmada (la sesión viaja por cookie; sin visores de terceros). */
export function abrirPdfEnPestana(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Descarga el archivo original (`?d=1` → `Content-Disposition: attachment`) con un enlace
 * temporal, sin abrir pestañas en blanco. Antes del clic comprueba con un `HEAD` que el enlace
 * responde (round 2026-10-28): front y API son orígenes distintos, el atributo `download` se ignora y
 * el clic es una NAVEGACIÓN; con un token vencido o un archivo borrado la pestaña entera pasaba a
 * mostrar el JSON del error. Ver `utils/descargaArchivo.ts`.
 *
 * Devuelve `true` si la descarga arrancó; `false` si no, y en ese caso ya se avisó el motivo
 * («El enlace venció…», «El archivo ya no existe.»): quien llama no debe anunciar un éxito.
 */
export function descargarArchivo(url: string, nombre: string): Promise<boolean> {
  return descargarVerificado(urlDeDescarga(url), nombre);
}
